import { Loader } from "@/components/loader/Loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createSetupIntent } from "./billingApi.ts";
import { useRefreshPaymentMethods } from "./useBilling.tsx";

/**
 * One Stripe.js instance for the whole app — `loadStripe` injects a script
 * tag, so calling it per dialog would reload it on every open. Null when no
 * publishable key is configured, which is how a non-billing environment
 * turns the add-card flow off rather than failing inside Stripe.js.
 */
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> | null {
  const key = import.meta.env["VITE_STRIPE_PUBLISHABLE_KEY"];
  if (!key) return null;
  stripePromise ??= loadStripe(key);
  return stripePromise;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Card entry happens inside Stripe's own iframe (the Payment Element), so no
 * card data ever touches our markup. We hand it a SetupIntent client secret
 * and, once confirmed, ask the server to re-read the vault so the list is
 * correct without waiting for the webhook.
 */
export const AddPaymentMethodDialog = ({ open, onOpenChange }: Props) => {
  const stripe = getStripe();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const setupIntent = useMutation({
    mutationFn: createSetupIntent,
    onSuccess: (data) => setClientSecret(data.clientSecret),
    onError: () => errorToast("Couldn't start adding a card"),
  });

  const { mutate: startSetupIntent, reset: resetSetupIntent } = setupIntent;
  useEffect(() => {
    if (open && stripe) {
      startSetupIntent();
      return;
    }
    setClientSecret(null);
    resetSetupIntent();
  }, [open, stripe, startSetupIntent, resetSetupIntent]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a payment method</DialogTitle>
          <DialogDescription>
            Your card is saved with Stripe and used for future Okthx charges, like your plan
            renewal. We never see or store the card number.
          </DialogDescription>
        </DialogHeader>
        {!stripe ? (
          <p className="text-sm text-muted-foreground">
            Payments aren't configured in this environment.
          </p>
        ) : clientSecret ? (
          <Elements stripe={stripe} options={{ clientSecret }}>
            <AddPaymentMethodForm onDone={() => onOpenChange(false)} />
          </Elements>
        ) : (
          <div className="py-6 flex justify-center">
            <Loader size="sm" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AddPaymentMethodForm = ({ onDone }: { onDone: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const refresh = useRefreshPaymentMethods();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    // `redirect: "if_required"` keeps 3DS-free cards inline; a card that
    // needs a challenge still gets Stripe's redirect flow.
    const result = await stripe.confirmSetup({ elements, redirect: "if_required" });
    setSubmitting(false);
    if (result.error) {
      errorToast(result.error.message ?? "Your card couldn't be saved");
      return;
    }
    refresh.mutate();
    onDone();
  };

  return (
    <>
      <PaymentElement />
      <DialogFooter>
        <Button
          onClick={handleSubmit}
          disabled={!stripe || submitting}
          data-statsig-name="billing-add-card-confirm"
        >
          {submitting ? "Saving…" : "Save card"}
        </Button>
      </DialogFooter>
    </>
  );
};
